import {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  createMeetingApiCall,
  getAllMeetingsApiCall,
  getMeetingsHistoryApiCall,
  getMeetingApiCall,
  requestToJoinAuthenticatedApiCall,
  requestToJoinUnauthenticatedApiCall,
  joinByCodeApiCall,
  validateJoinCodeApiCall,
  joinByCodeUnauthenticatedApiCall,
  joinByTokenApiCall,
  joinByTokenUnauthenticatedApiCall,
  cancelRequestToJoinApiCall,
  deleteMeetingApiCall,
  updateMeetingApiCall,
  regeneratePasscodeApiCall,
  createRecurringMeetingApiCall,
  getRecurringMeetingApiCall,
  updateRecurringMeetingApiCall,
  cancelRecurringMeetingApiCall,
} from "../API/meetings/meetingsService";
import moment from "moment";

export const MeetingsContext = createContext({
  meetings: [],
  currentMeeting: [],
  currentMeetingRef: [],
  meetingsRef: [],
  createMeeting: () => {},
  updateMeeting: () => {},
  regeneratePasscode: () => {},
  getMeetings: () => {},
  pastMeetings: [],
  pastMeetingsHasMore: true,
  getMeetingsHistory: () => {},
  getMeetingById: () => {},
  deleteMeeting: () => {},
  requestToJoinAuthenticated: () => {},
  requestToJoinUnauthenticated: () => {},
  joinByCode: () => {},
  validateJoinCode: () => {},
  joinByCodeUnauthenticated: () => {},
  joinByToken: () => {},
  joinByTokenUnauthenticated: () => {},
  cancelRequestToJoin: () => {},
  setCurrentMeeting: () => {},
  createRecurringMeeting: () => {},
  getRecurringMeeting: () => {},
  updateRecurringMeeting: () => {},
  cancelRecurringMeeting: () => {},
});

const MeetingsContextProvider = ({ children }) => {
  const [meetings, setMeetings] = useState([]);
  const [currentMeeting, setCurrentMeeting] = useState();
  const [pastMeetings, setPastMeetings] = useState([]);
  const [pastMeetingsCursor, setPastMeetingsCursor] = useState(null);
  const [pastMeetingsHasMore, setPastMeetingsHasMore] = useState(true);

  const meetingsRef = useRef(null);
  const meetingsFilterRef = useRef({});
  const currentMeetingRef = useRef(null);
  const handleMeetingsResponseFnRef = useRef(() => {});

  const createMeeting = useCallback(
    async (dataObj, onSuccess, errorCallback) => {
      try {
        await createMeetingApiCall(
          dataObj,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updateMeeting = useCallback(
    async (objData, onSuccess, errorCallback) => {
      try {
        await updateMeetingApiCall(
          objData,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const regeneratePasscode = useCallback(
    async (meetingId, onSuccess, errorCallback) => {
      try {
        await regeneratePasscodeApiCall(
          meetingId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getMeetings = useCallback(async (filters, onSuccess, errorCallback) => {
    if (filters && Object.keys(filters).length > 0) {
      meetingsFilterRef.current = filters;
    }
    const { dateFrom, dateTo, q } = meetingsFilterRef.current;
    try {
      await getAllMeetingsApiCall(
        { dateFrom, dateTo, q },
        function (response) {
          handleMeetingsResponseFnRef.current(response);
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const handleMeetingsResponse = useCallback((response) => {
    const sortedCalls = response.data.meetings.sort((a, b) =>
      moment(a.start_at).isAfter(moment(b.start_at)) ? 1 : -1
    );
    meetingsRef.current = sortedCalls;
    setMeetings(sortedCalls);
  }, []);
  handleMeetingsResponseFnRef.current = handleMeetingsResponse;

  const getMeetingsHistory = useCallback(
    async (
      { before, limit, dateFrom, dateTo, q, reset } = {},
      onSuccess,
      errorCallback
    ) => {
      try {
        await getMeetingsHistoryApiCall(
          { before, limit, dateFrom, dateTo, q },
          function (response) {
            const { meetings: newMeetings, next_cursor } = response.data;
            setPastMeetings((prev) =>
              reset ? newMeetings : [...prev, ...newMeetings]
            );
            setPastMeetingsCursor(next_cursor);
            setPastMeetingsHasMore(!!next_cursor);
            onSuccess?.(response);
          },
          function (error) {
            errorCallback?.(error);
          }
        );
      } catch (err) {
        errorCallback?.(err);
      }
    },
    []
  );

  const getMeetingById = useCallback(
    async (meetingId, onSuccess, errorCallback) => {
      try {
        await getMeetingApiCall(
          meetingId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );


  const deleteMeeting = useCallback(
    async (meetingId, onSuccess, errorCallback) => {
      try {
        await deleteMeetingApiCall(
          meetingId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const requestToJoinAuthenticated = useCallback(
    async (meetingId, onSuccess, errorCallback) => {
      try {
        await requestToJoinAuthenticatedApiCall(
          meetingId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const requestToJoinUnauthenticated = useCallback(
    async (
      meetingId,
      name,
      call_language,
      chat_language,
      onSuccess,
      errorCallback
    ) => {
      try {
        await requestToJoinUnauthenticatedApiCall(
          meetingId,
          name,
          call_language,
          chat_language,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const joinByCode = useCallback(
    async (displayId, passcode, onSuccess, errorCallback) => {
      try {
        await joinByCodeApiCall(
          displayId,
          passcode,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const validateJoinCode = useCallback(
    async (displayId, passcode, onSuccess, errorCallback) => {
      try {
        await validateJoinCodeApiCall(
          displayId,
          passcode,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const joinByCodeUnauthenticated = useCallback(
    async (
      displayId,
      passcode,
      name,
      call_language,
      chat_language,
      onSuccess,
      errorCallback
    ) => {
      try {
        await joinByCodeUnauthenticatedApiCall(
          displayId,
          passcode,
          name,
          call_language,
          chat_language,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const joinByToken = useCallback(
    async (token, onSuccess, errorCallback) => {
      try {
        await joinByTokenApiCall(
          token,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const joinByTokenUnauthenticated = useCallback(
    async (token, name, call_language, chat_language, onSuccess, errorCallback) => {
      try {
        await joinByTokenUnauthenticatedApiCall(
          token,
          name,
          call_language,
          chat_language,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const cancelRequestToJoin = useCallback(
    async (meetingId, onSuccess, errorCallback) => {
      try {
        await cancelRequestToJoinApiCall(
          meetingId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const createRecurringMeeting = useCallback(
    async (dataObj, onSuccess, errorCallback) => {
      try {
        await createRecurringMeetingApiCall(
          dataObj,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getRecurringMeeting = useCallback(
    async (seriesId, onSuccess, errorCallback) => {
      try {
        await getRecurringMeetingApiCall(
          seriesId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updateRecurringMeeting = useCallback(
    async (seriesId, dataObj, onSuccess, errorCallback) => {
      try {
        await updateRecurringMeetingApiCall(
          seriesId,
          dataObj,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const cancelRecurringMeeting = useCallback(
    async (seriesId, dataObj, onSuccess, errorCallback) => {
      try {
        await cancelRecurringMeetingApiCall(
          seriesId,
          dataObj,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      meetings,
      createMeeting,
      meetingsRef,
      updateMeeting,
      regeneratePasscode,
      getMeetings,
      pastMeetings,
      pastMeetingsHasMore,
      getMeetingsHistory,
      getMeetingById,
      deleteMeeting,
      requestToJoinAuthenticated,
      requestToJoinUnauthenticated,
      joinByCode,
      validateJoinCode,
      joinByCodeUnauthenticated,
      joinByToken,
      joinByTokenUnauthenticated,
      cancelRequestToJoin,
      currentMeeting,
      setCurrentMeeting,
      currentMeetingRef,
      createRecurringMeeting,
      getRecurringMeeting,
      updateRecurringMeeting,
      cancelRecurringMeeting,
    }),
    [
      meetings,
      createMeeting,
      meetingsRef,
      updateMeeting,
      regeneratePasscode,
      getMeetings,
      pastMeetings,
      pastMeetingsHasMore,
      getMeetingsHistory,
      getMeetingById,
      deleteMeeting,
      requestToJoinAuthenticated,
      requestToJoinUnauthenticated,
      joinByCode,
      validateJoinCode,
      joinByCodeUnauthenticated,
      joinByToken,
      joinByTokenUnauthenticated,
      cancelRequestToJoin,
      currentMeeting,
      setCurrentMeeting,
      currentMeetingRef,
      createRecurringMeeting,
      getRecurringMeeting,
      updateRecurringMeeting,
      cancelRecurringMeeting,
    ]
  );

  return (
    <MeetingsContext.Provider value={contextValue}>
      {children}
    </MeetingsContext.Provider>
  );
};

export const useMeetingsService = () => useContext(MeetingsContext);

export default MeetingsContextProvider;
